/**
 * Case studies.
 *
 * Every line here has to be defensible in front of the customer it
 * describes. That constraint is why there is one entry and not eight, and
 * why the `outcome` field carries no metrics: we have not published figures
 * the customer has agreed to, and an unverifiable number on this page would
 * be worth less than the honest sentence it replaced.
 *
 * Agent Labista used to sit here. It was removed because it has no users —
 * a case study needs an outcome, and "we built it and nobody has run it
 * yet" is a product page, not a case study. It lives on /labista instead.
 *
 * The Hazri Book customer is never named. Their attendance records carry
 * worker names, Iqama numbers and photographs under Saudi PDPL.
 */

export interface CaseStudy {
  slug: string;
  product: string;
  /** Headline framing of the engagement, not a slogan. */
  title: string;
  sector: string;
  market: string;
  /** Rendered as the four-part narrative: problem → approach → technology → outcome. */
  problem: string;
  approach: string;
  technology: string[];
  outcome: string;
  /** A single sentence pulled out as the visual anchor of the study. */
  pullQuote: string;
  shot?: { src: string; alt: string };
  href: string;
  accent: "emerald" | "violet";
}

export const CASE_STUDIES: CaseStudy[] = [
  {
    slug: "hazri-book",
    product: "Hazri Book",
    title: "Turning an attendance register into evidence",
    sector: "Construction and contracting",
    market: "Saudi Arabia",
    problem:
      "A contracting company's payroll ran on a paper hazri register and a supervisor's memory. Attendance was routinely marked from the site office rather than at the gate, overtime was agreed after it had been worked, and every month-end produced the same argument: hours the company could not prove and the client would not pay for.",
    approach:
      "We rejected the usual framing. Most attendance software tries to stop the worker cheating; the real problem was that the record could not survive scrutiny weeks later. So we designed the punch to produce evidence — the person, the place, the time and a sealed record — and we made the system flag what it could not capture instead of discarding it. A worker whose photo failed still gets a record, marked as an exception for a human to resolve. Throwing that punch away would have deleted the proof that someone was on site.",
    technology: [
      "Offline-first mobile client, syncing when the site gets signal",
      "Photo capture, GPS reading and server-set timestamp at every punch",
      "Exception flagging rather than silent rejection",
      "Multi-tenant isolation — no company can see another's workforce",
      "Arabic and English, right-to-left throughout the interface",
      "Overtime authorisation ahead of payability, month-end lock, payroll exports",
    ],
    outcome:
      "In production, not a pilot. The company closes the month on records that carry their own proof, and overtime is authorised before it becomes payable rather than argued about afterwards.",
    pullQuote:
      "A register records a claim. It does not record a fact.",
    shot: {
      src: "/hazribook/shots/dashboard.png",
      alt: "Hazri Book company dashboard showing labour cost, live presence across sites, and exception flags awaiting review",
    },
    href: "/hazribook",
    accent: "emerald",
  },
];
